import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Driver, Alert, Delivery, CargoItem } from '../data/mockData';
import { Truck, MapPin, Clock, Phone, Navigation, AlertTriangle, ShieldAlert, PackageIcon, Check, ChevronDown } from 'lucide-react';
interface DriverDetailProps {
  driver: Driver | null;
  alerts: Alert[];
  deliveries?: Delivery[];
  scrollToRouteTs?: number | null;
  selectedDeliveryRef?: string | null;
}
export function DriverDetail({
  driver,
  alerts,
  deliveries = [],
  scrollToRouteTs,
  selectedDeliveryRef
}: DriverDetailProps) {
  const routeRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollToRouteTs && routeRef.current && scrollContainerRef.current) {
      const timer = setTimeout(() => {
        routeRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [scrollToRouteTs]);
  if (!driver) {
    return null;
  }
  // Build a map from stop address to full delivery object
  const stopDeliveryMap = new Map<string, Delivery>();
  deliveries.forEach(d => {
    stopDeliveryMap.set(d.destination, d);
  });
  const hosPercent = driver.hosDriven / 11 * 100;
  const isHosCritical = driver.hosDriven > 10;
  const isOffline = driver.status === 'offline';
  return <motion.div initial={{
    x: 380,
    opacity: 0
  }} animate={{
    x: 0,
    opacity: 1
  }} exit={{
    x: 380,
    opacity: 0
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 250
  }} className="w-[380px] h-full glass-panel border-l border-slate-800 flex flex-col shrink-0 z-10 bg-slate-950/90" data-id="element-142">
      {/* Offline Banner */}
      {driver.signalBars === 0 && <div className="px-4 py-2.5 bg-[#bac3d4] border-b border-[#a3adbf] flex items-center justify-between shrink-0" data-id="element-143">
          <div className="flex items-center gap-2 text-slate-900" data-id="element-144">
            <img src="https://cdn.magicpatterns.com/uploads/qtiWE7RgqX6uR9cCddEQgD/android_cell_4_bar_off_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24.svg" alt="No signal" className="w-4 h-4 brightness-0" data-id="element-145" />
            <span className="text-xs font-semibold" data-id="element-146">Truck Offline</span>
          </div>
          <span className="text-[10px] font-mono text-slate-900/60" data-id="element-147">
            Last update {driver.lastPing || 'unknown'}
          </span>
        </div>}

      {/* Header - Truck ID only (sticky) */}
      <div className="px-5 py-3 border-b border-slate-800 relative overflow-hidden shrink-0" data-id="element-148">
        <div className="flex justify-between items-center relative z-10" data-id="element-149">
          <h2 className="text-base font-bold text-slate-100 font-mono tracking-tight flex items-center gap-2" data-id="element-150">
            <Truck className="w-4 h-4 text-slate-500" data-id="element-151" />
            {driver.truckId}
            <SignalBars bars={driver.signalBars ?? 4} data-id="element-152" />
          </h2>
          <StatusBadge status={driver.status} data-id="element-153" />
        </div>
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto scrollbar-hide" data-id="element-154">
        {/* Driver Info (scrollable) */}
        <div className="px-5 pt-4 pb-3 border-b border-slate-800" data-id="element-155">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-3" data-id="element-156">
            <span data-id="element-157">{driver.name}</span>
            <span data-id="element-158">•</span>
            <span data-id="element-159">Clearance: {driver.truckHeight}</span>
          </div>

          {/* Condensed HOS Bar */}
          <div className="relative z-10" data-id="element-160">
            <div className="flex items-center justify-between mb-1.5" data-id="element-161">
              <span className="text-[10px] text-slate-500 flex items-center gap-1.5" data-id="element-162">
                <Clock className="w-3 h-3" data-id="element-163" />
                HOS
              </span>
              <span className="text-[10px] font-mono text-slate-400" data-id="element-164">
                {driver.hosDriven.toFixed(1)} / 11.0 hrs
                {isHosCritical && <span className="text-brand-red ml-1.5 font-semibold" data-id="element-165">
                    · {((11 - driver.hosDriven) * 60).toFixed(0)}m left
                  </span>}
              </span>
            </div>
            <div className="h-1.5 bg-slate-900 rounded-full overflow-hidden border border-slate-800" data-id="element-166">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${Math.min(hosPercent, 100)}%`
            }} className={`h-full rounded-full ${isHosCritical ? 'bg-brand-red' : hosPercent > 80 ? 'bg-brand-amber' : 'bg-brand-green'}`} data-id="element-167" />
            </div>
          </div>

          {isOffline && <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 flex items-start gap-3 mt-3" data-id="element-168">
              <ShieldAlert className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" data-id="element-169" />
              <div data-id="element-170">
                <p className="text-xs font-medium text-slate-300" data-id="element-171">Data Stale</p>
                <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-172">
                  Last GPS ping: {driver.lastPing}. ETAs are estimated based on
                  last known position.
                </p>
              </div>
            </div>}
        </div>

        {/* Active Alerts */}
        {alerts.length > 0 && <div className="p-3 space-y-2" data-id="element-173">
            {alerts.map(alert => <div key={alert.id} className={`px-3 py-2.5 rounded-lg border ${alert.severity === 'critical' ? 'bg-brand-red/10 border-brand-red/30' : alert.severity === 'warning' ? 'bg-brand-amber/10 border-brand-amber/30' : 'bg-brand-blue/10 border-brand-blue/30'}`} data-id="element-174">
                <div className="flex items-start gap-2.5" data-id="element-175">
                  <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${alert.severity === 'critical' ? 'text-brand-red' : alert.severity === 'warning' ? 'text-brand-amber' : 'text-brand-blue'}`} data-id="element-176" />
                  <div className="flex-1 min-w-0" data-id="element-177">
                    <h4 className="text-xs font-semibold truncate text-slate-100" data-id="element-178">
                      {alert.title}
                    </h4>
                    <p className="text-[11px] text-slate-400 leading-snug mt-1 line-clamp-2" data-id="element-179">
                      {alert.description}
                    </p>
                    {(alert.severity === 'critical' || alert.severity === 'warning') && alert.actionHint && alert.actionHint.toLowerCase() !== 'no action needed' && <div className="flex gap-2 mt-2" data-id="element-180">
                          <button className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${alert.severity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} data-id="element-181">
                            <Phone className="w-3 h-3" data-id="element-182" />
                            Contact
                          </button>
                          <button className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${alert.severity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} data-id="element-183">
                            <Navigation className="w-3 h-3" data-id="element-184" />
                            Reroute
                          </button>
                        </div>}
                  </div>
                </div>
              </div>)}
          </div>}

        {/* Route List */}
        <div ref={routeRef} className="p-5" data-id="element-185">
          <h3 className="text-sm font-medium text-slate-300 flex items-center mb-4" data-id="element-186">
            <span className="flex items-center gap-2" data-id="element-187">
              <MapPin className="w-4 h-4 text-slate-500" data-id="element-188" />
              Stops
            </span>
            <span className="ml-auto text-[11px] text-slate-500 font-normal" data-id="element-189">
              {driver.route.filter(s => s.status !== 'completed').length} /{' '}
              {driver.route.length} Remaining
            </span>
          </h3>

          <div className="space-y-0 relative" data-id="element-190">
            <div className="absolute top-3 bottom-8 left-[11px] w-0.5 bg-slate-800" data-id="element-191" />

            {driver.route.map((stop, idx) => {
            const nextStopIdx = driver.route.findIndex(s => s.status !== 'completed');
            const isNextStop = idx === nextStopIdx;
            const nextStopColor = driver.status === 'critical' ? 'brand-red' : driver.status === 'warning' ? 'brand-amber' : 'white';
            return <div key={stop.id} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-192">
                  <div className="relative z-10 mt-1" data-id="element-193">
                    {stop.status === 'completed' ? <div className="w-6 h-6 rounded-full flex items-center justify-center bg-brand-green" data-id="element-194">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-195" />
                      </div> : isNextStop ? <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 bg-slate-950 ${driver.status === 'critical' ? 'border-brand-red' : driver.status === 'warning' ? 'border-brand-amber' : 'border-white'}`} data-id="element-196">
                        <div className={`w-2 h-2 rounded-full ${driver.status === 'critical' ? 'bg-brand-red' : driver.status === 'warning' ? 'bg-brand-amber' : 'bg-white'}`} data-id="element-197" />
                      </div> : <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 bg-slate-950 border-slate-600" data-id="element-198">
                        <div className="w-2 h-2 rounded-full bg-slate-600" data-id="element-199" />
                      </div>}
                  </div>

                  {(() => {
                const delivery = stopDeliveryMap.get(stop.address);
                const isHighlighted = delivery && selectedDeliveryRef === delivery.refNumber;
                const isCriticalNext = stop.status !== 'completed' && driver.status === 'critical';
                const statusBadge = isCriticalNext ? 'text-brand-red border-brand-red/30 bg-brand-red/10' : stop.status === 'at-risk' ? 'text-brand-amber border-brand-amber/30 bg-brand-amber/10' : stop.status === 'completed' ? 'text-brand-green border-brand-green/30 bg-brand-green/10' : 'text-slate-400 border-slate-700 bg-slate-800';
                return <div className={`flex-1 rounded-lg p-3 border transition-all ${isHighlighted ? 'bg-slate-800/60 border-slate-600 shadow-[0_0_15px_rgba(0,0,0,0.2)]' : 'bg-slate-900/50 border-slate-800'}`} data-id="element-200">
                        {delivery && <div className="flex items-center justify-between mb-1" data-id="element-201">
                            <span className={`text-[10px] font-mono font-semibold tracking-wide ${stop.status === 'completed' ? 'text-slate-600' : 'text-slate-100'}`} data-id="element-202">
                              {delivery.refNumber}
                            </span>
                            {(stop.status !== 'pending' || isCriticalNext) && <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusBadge}`} data-id="element-203">
                                {isCriticalNext ? 'CRITICAL' : stop.status === 'at-risk' ? 'AT RISK' : stop.status === 'completed' ? 'DONE' : stop.status.toUpperCase()}
                              </span>}
                          </div>}
                        <p className={`text-sm font-medium mb-1 ${stop.status === 'completed' ? 'text-slate-500' : 'text-slate-200'}`} data-id="element-204">
                          {stop.address}
                        </p>
                        {delivery && stop.status !== 'completed' && <div className="flex items-center gap-1.5 mb-1.5" data-id="element-205">
                            <PackageIcon className="w-3 h-3 text-slate-600" data-id="element-206" />
                            <span className="text-[11px] text-slate-400 truncate" data-id="element-207">
                              {delivery.commodity}
                            </span>
                            <span className="text-[10px] text-slate-600" data-id="element-208">
                              ·
                            </span>
                            <span className="text-[10px] text-slate-500 shrink-0" data-id="element-209">
                              {delivery.pallets}p /{' '}
                              {(delivery.weight / 1000).toFixed(1)}k lbs
                            </span>
                          </div>}
                        {stop.status === 'completed' ? <div className="flex justify-between items-center text-xs" data-id="element-210">
                            <span className="font-mono text-slate-500" data-id="element-211">
                              {(() => {
                        // Compute deterministic ARR/DEP from ETA
                        const [h, m] = stop.eta.replace('~', '').split(':').map(Number);
                        const arrMin = h * 60 + m - (idx * 7 + 3) % 12;
                        const depMin = arrMin + 12 + idx * 5 % 18;
                        const arrH = Math.floor(arrMin / 60) % 24;
                        const arrM = arrMin % 60;
                        const depH = Math.floor(depMin / 60) % 24;
                        const depM = depMin % 60;
                        return `ARR: ${String(arrH).padStart(2, '0')}:${String(arrM).padStart(2, '0')}    DEP: ${String(depH).padStart(2, '0')}:${String(depM).padStart(2, '0')}`;
                      })()}
                            </span>
                            {(() => {
                      // Determine on-time status from ETA vs window
                      const [h, m] = stop.eta.replace('~', '').split(':').map(Number);
                      const etaMin = h * 60 + m;
                      const windowStart = stop.window.split(' - ')[0];
                      const [wh, wm] = windowStart.split(':').map(Number);
                      const windowMin = wh * 60 + wm;
                      const diff = etaMin - windowMin;
                      const status = diff < 5 ? 'Early' : diff > 90 ? 'Late' : 'On Time';
                      const color = status === 'Late' ? 'text-brand-amber' : 'text-brand-green';
                      return <span className={`flex items-center gap-1 text-[10px] font-medium ${color}`} data-id="element-212">
                                  <Clock className="w-3 h-3" data-id="element-213" />
                                  {status}
                                </span>;
                    })()}
                          </div> : <div className="flex justify-between items-center text-xs" data-id="element-214">
                            <div className="flex items-center gap-2" data-id="element-215">
                              <span className={`font-mono ${isCriticalNext ? 'text-brand-red font-semibold' : stop.status === 'at-risk' ? 'text-brand-amber font-semibold' : 'text-slate-400'}`} data-id="element-216">
                                ETA: {stop.eta}
                              </span>
                              <span className="text-slate-600" data-id="element-217">·</span>
                              <span className="text-slate-500 font-mono" data-id="element-218">
                                {stop.distance || (() => {
                          const pendingStops = driver.route.filter((s, i) => i >= idx && s.status !== 'completed');
                          const posInPending = pendingStops.indexOf(stop);
                          const base = (driver.id.charCodeAt(driver.id.length - 1) * 7 + idx * 11) % 20 + 5 + posInPending * 8;
                          return `${base} mi`;
                        })()}
                              </span>
                            </div>
                            <span className="text-slate-500" data-id="element-219">
                              {stop.window !== 'N/A' ? `Window: ${stop.window}` : ''}
                            </span>
                          </div>}
                      </div>;
              })()}
                </div>;
          })}
          </div>
        </div>

        {/* Cargo Manifest */}
        {driver.cargo.length > 0 && <CargoManifest cargo={driver.cargo} data-id="element-220" />}
      </div>

      {/* Action Strip */}
      <div className="p-4 border-t border-slate-800 bg-slate-900/50 grid grid-cols-2 gap-2" data-id="element-221">
        <button className="flex items-center justify-center gap-2 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium rounded-lg transition-colors" data-id="element-222">
          <Phone className="w-4 h-4" data-id="element-223" /> Contact
        </button>
        <button className="flex items-center justify-center gap-2 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium rounded-lg transition-colors" data-id="element-224">
          <Navigation className="w-4 h-4" data-id="element-225" /> Reroute
        </button>
      </div>
    </motion.div>;
}
function StatusBadge({
  status
}: {
  status: Severity;
}) {
  const config = {
    critical: {
      color: 'text-brand-red',
      bg: 'bg-brand-red/10',
      border: 'border-brand-red/30',
      label: 'CRITICAL'
    },
    warning: {
      color: 'text-brand-amber',
      bg: 'bg-brand-amber/10',
      border: 'border-brand-amber/30',
      label: 'WARNING'
    },
    offline: {
      color: 'text-slate-400',
      bg: 'bg-slate-800',
      border: 'border-slate-600',
      label: 'OFFLINE'
    },
    info: {
      color: 'text-brand-blue',
      bg: 'bg-brand-blue/10',
      border: 'border-brand-blue/30',
      label: 'INFO'
    },
    normal: {
      color: 'text-brand-green',
      bg: 'bg-brand-green/10',
      border: 'border-brand-green/30',
      label: 'ON TRACK'
    }
  }[status];
  return <span className={`text-[10px] font-bold px-2 py-1 rounded border tracking-wider ${config.color} ${config.bg} ${config.border}`} data-id="element-226">
      {config.label}
    </span>;
}
function SignalBars({
  bars
}: {
  bars: number;
}) {
  if (bars === 0) {
    return <img src="https://cdn.magicpatterns.com/uploads/qtiWE7RgqX6uR9cCddEQgD/android_cell_4_bar_off_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24.svg" alt="No signal" className="w-4 h-4 ml-0.5 opacity-50" data-id="element-227" />;
  }
  const heights = [4, 7, 10, 13];
  return <svg width="14" height="14" viewBox="0 0 14 14" className="ml-0.5" data-id="element-228">
      {heights.map((h, i) => <rect key={i} x={i * 3.5} y={14 - h} width="2.5" height={h} rx="0.5" fill={i < bars ? '#cbd5e1' : '#334155'} data-id="element-229" />)}
    </svg>;
}
function CargoManifest({
  cargo
}: {
  cargo: CargoItem[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  if (cargo.length === 0) return null;
  return <div className="p-5 border-t border-slate-800" data-id="element-230">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between cursor-pointer" data-id="element-231">
        <div data-id="element-232">
          <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2" data-id="element-233">
            <PackageIcon className="w-4 h-4 text-slate-500" data-id="element-234" />
            Cargo Manifest
          </h3>
          <p className="text-[10px] text-slate-500 mt-1" data-id="element-235">
            {cargo.reduce((sum, c) => sum + c.pallets, 0)} pallets ·{' '}
            {(cargo.reduce((sum, c) => sum + c.weight, 0) / 1000).toFixed(1)}k
            lbs total
          </p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-236" />
      </button>
      {isOpen && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="space-y-2 mt-4 overflow-hidden" data-id="element-237">
          {cargo.map(item => <div key={item.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3" data-id="element-238">
              <div className="flex items-center justify-between mb-1.5" data-id="element-239">
                <span className="text-[10px] font-mono text-slate-500" data-id="element-240">
                  {item.bol}
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${item.status === 'delivered' ? 'text-brand-green' : 'text-slate-400'}`} data-id="element-241">
                  {item.status === 'in-transit' ? 'IN TRANSIT' : item.status.toUpperCase()}
                </span>
              </div>
              <p className="text-xs font-medium mb-1.5 text-slate-200" data-id="element-242">
                {item.commodity}
              </p>
              <div className="flex gap-3 text-[10px] text-slate-500" data-id="element-243">
                <span data-id="element-244">{item.weight.toLocaleString()} lbs</span>
                <span data-id="element-245">·</span>
                <span data-id="element-246">
                  {item.pallets} {item.pallets === 1 ? 'pallet' : 'pallets'}
                </span>
              </div>
            </div>)}
        </motion.div>}
    </div>;
}